/**
 * Admin Dashboard — MERIDIAN Operations Console.
 *
 * A next-generation luxury-light enterprise operations platform.
 * Tri-column cockpit shell:
 *   - Slim CommandBar (top)
 *   - Grouped NavRail (left)
 *   - Main content with 9 modular operational panels
 *   - Floating AI Insights rail (right)
 *
 * Theme: warm ivory + pearl gray surfaces, muted emerald · slate-blue ·
 * copper · olive accents. Geist typography. Recharts for executive-grade
 * data visualisation. Pure CSS animations.
 */

export const ADMIN_DASHBOARD_FILES = {
  'src/index.css': `@import url('https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700&family=Geist+Mono:wght@400;500;600&display=swap');

* { box-sizing: border-box; }
html, body, #root { min-height: 100%; }
body {
  margin: 0;
  font-family: 'Geist', system-ui, -apple-system, 'Segoe UI', sans-serif;
  background: #F4F1EA;
  color: #1C1F1A;
  -webkit-font-smoothing: antialiased;
  font-feature-settings: 'ss01', 'cv11';
}
h1, h2, h3, h4, h5, h6, p { margin: 0; }
input, button, textarea, select { font-family: inherit; }

/* ========================================================
   LUXURY LIGHT ENTERPRISE TOKENS
   ======================================================== */
:root {
  --md-bg:        #F4F1EA;
  --md-surface:   #FBF9F3;
  --md-card:      #FFFFFF;
  --md-subtle:    #EFEDE6;
  --md-stone:     #E4E0D6;
  --md-border:    #E1DCCF;
  --md-border-2:  #ECE7DA;

  --md-text:      #1C1F1A;
  --md-text-2:    #54574F;
  --md-text-3:    #8A8B83;
  --md-text-4:    #B5B4AA;

  --md-emerald:        #4A6B5C;
  --md-emerald-soft:   rgba(74, 107, 92, 0.10);
  --md-emerald-edge:   rgba(74, 107, 92, 0.22);

  --md-slate:          #5B6B7D;
  --md-slate-soft:     rgba(91, 107, 125, 0.10);
  --md-slate-edge:     rgba(91, 107, 125, 0.22);

  --md-copper:         #A6755A;
  --md-copper-soft:    rgba(166, 117, 90, 0.12);
  --md-copper-edge:    rgba(166, 117, 90, 0.25);

  --md-olive:          #7A7B47;
  --md-olive-soft:     rgba(122, 123, 71, 0.12);
  --md-olive-edge:     rgba(122, 123, 71, 0.24);

  --md-graphite:       #2A2D27;
  --md-graphite-soft:  rgba(28, 31, 26, 0.06);
}

/* ========================================================
   TYPOGRAPHY UTILITIES
   ======================================================== */
.md-mono   { font-family: 'Geist Mono', ui-monospace, 'SF Mono', monospace; font-feature-settings: 'tnum', 'zero'; }
.md-tnum   { font-feature-settings: 'tnum'; font-variant-numeric: tabular-nums; }
.md-eyebrow { font-size: 10.5px; font-weight: 500; letter-spacing: 0.16em; text-transform: uppercase; color: var(--md-text-3); }
.md-label   { font-size: 11px; font-weight: 500; color: var(--md-text-3); letter-spacing: 0.02em; }

/* ========================================================
   SHELL
   ======================================================== */
.md-shell {
  display: grid;
  grid-template-columns: 232px minmax(0, 1fr) 320px;
  grid-template-rows: auto minmax(0, 1fr);
  min-height: 100vh;
  width: 100%;
  background: var(--md-bg);
  color: var(--md-text);
}
@media (max-width: 1280px) {
  .md-shell { grid-template-columns: 220px minmax(0, 1fr); }
  .md-shell .md-ai-rail { display: none; }
}
@media (max-width: 900px) {
  .md-shell { grid-template-columns: minmax(0, 1fr); }
  .md-shell .md-nav-rail { display: none; }
}

.md-cmd-bar {
  grid-column: 1 / -1;
  grid-row: 1;
  position: sticky;
  top: 0;
  z-index: 30;
  min-width: 0;
  height: 56px;
}
.md-nav-rail {
  grid-column: 1;
  grid-row: 2;
  min-width: 0;
  position: sticky;
  top: 56px;
  align-self: start;
  max-height: calc(100vh - 56px);
  overflow-y: auto;
}
.md-content {
  grid-column: 2;
  grid-row: 2;
  min-width: 0;
}
.md-ai-rail {
  grid-column: 3;
  grid-row: 2;
  min-width: 0;
  position: sticky;
  top: 56px;
  align-self: start;
  max-height: calc(100vh - 56px);
  overflow-y: auto;
}
@media (max-width: 1280px) {
  .md-ai-rail { grid-column: auto; }
}
@media (max-width: 900px) {
  .md-nav-rail { grid-column: auto; }
}

/* ========================================================
   SURFACES
   ======================================================== */
.md-panel {
  background: var(--md-card);
  border: 1px solid var(--md-border);
  border-radius: 14px;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.5) inset,
    0 4px 14px -8px rgba(28, 31, 26, 0.08);
}
.md-panel-flat {
  background: var(--md-surface);
  border: 1px solid var(--md-border-2);
  border-radius: 14px;
}
.md-panel-deep {
  background: linear-gradient(165deg, #1F221C 0%, #2A2D27 100%);
  color: #ECE9DD;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 14px;
  box-shadow: 0 16px 36px -22px rgba(28, 31, 26, 0.4);
}

.md-divider { height: 1px; background: var(--md-border-2); width: 100%; }
.md-divider-v { width: 1px; background: var(--md-border-2); align-self: stretch; }

/* ========================================================
   BUTTONS / CHIPS
   ======================================================== */
.md-btn {
  display: inline-flex; align-items: center; gap: 6px;
  height: 30px;
  padding: 0 12px;
  border-radius: 8px;
  font-size: 12px; font-weight: 500;
  border: 1px solid var(--md-border);
  background: var(--md-card);
  color: var(--md-text);
  cursor: pointer;
  transition: background .2s, border-color .2s, transform .2s;
  white-space: nowrap;
}
.md-btn:hover { background: var(--md-surface); border-color: var(--md-stone); }
.md-btn-primary {
  background: var(--md-graphite);
  color: #ECE9DD;
  border-color: var(--md-graphite);
}
.md-btn-primary:hover { background: #1F221C; border-color: #1F221C; }
.md-btn-ghost {
  background: transparent;
  border-color: transparent;
  color: var(--md-text-2);
}
.md-btn-ghost:hover { background: var(--md-graphite-soft); color: var(--md-text); }

.md-chip {
  display: inline-flex; align-items: center; gap: 5px;
  height: 22px;
  padding: 0 9px;
  border-radius: 6px;
  font-size: 10.5px; font-weight: 500; letter-spacing: 0.02em;
  border: 1px solid var(--md-border);
  background: var(--md-surface);
  color: var(--md-text-2);
  white-space: nowrap;
}
.md-chip-emerald { background: var(--md-emerald-soft); border-color: var(--md-emerald-edge); color: var(--md-emerald); }
.md-chip-slate   { background: var(--md-slate-soft);   border-color: var(--md-slate-edge);   color: var(--md-slate); }
.md-chip-copper  { background: var(--md-copper-soft);  border-color: var(--md-copper-edge);  color: var(--md-copper); }
.md-chip-olive   { background: var(--md-olive-soft);   border-color: var(--md-olive-edge);   color: var(--md-olive); }
.md-chip-dark    { background: var(--md-graphite); border-color: var(--md-graphite); color: #ECE9DD; }

.md-dot { width: 6px; height: 6px; border-radius: 999px; background: currentColor; flex-shrink: 0; }

/* ========================================================
   NAV RAIL
   ======================================================== */
.md-nav-item {
  position: relative;
  display: flex; align-items: center; gap: 10px;
  padding: 7px 11px;
  border-radius: 8px;
  font-size: 12.5px;
  color: var(--md-text-2);
  cursor: pointer;
  transition: background .18s, color .18s;
}
.md-nav-item:hover { background: var(--md-graphite-soft); color: var(--md-text); }
.md-nav-item.active {
  background: var(--md-card);
  color: var(--md-text);
  border: 1px solid var(--md-border);
  box-shadow: 0 1px 0 rgba(255,255,255,0.5) inset, 0 4px 10px -8px rgba(28,31,26,0.1);
}
.md-nav-item.active::before {
  content: '';
  position: absolute;
  left: -6px; top: 50%; transform: translateY(-50%);
  width: 2px; height: 14px;
  background: var(--md-graphite);
  border-radius: 2px;
}
.md-nav-item .md-nav-badge {
  margin-left: auto;
  font-family: 'Geist Mono', monospace;
  font-size: 10.5px;
  color: var(--md-text-3);
}

/* ========================================================
   ANIMATIONS — subtle, enterprise
   ======================================================== */
@keyframes md-rise {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes md-fade {
  from { opacity: 0; } to { opacity: 1; }
}
@keyframes md-blink {
  0%, 100% { opacity: 1; } 50% { opacity: 0.35; }
}
@keyframes md-shimmer {
  0%, 100% { opacity: 0.5; } 50% { opacity: 1; }
}
@keyframes md-pulse-ring {
  0%   { transform: scale(1);   opacity: 0.6; }
  100% { transform: scale(2.6); opacity: 0; }
}
@keyframes md-dash {
  to { stroke-dashoffset: -240; }
}

.md-rise   { animation: md-rise .55s cubic-bezier(.2,.85,.25,1) both; }
.md-fade   { animation: md-fade .4s ease both; }
.md-blink  { animation: md-blink 1.6s ease-in-out infinite; }
.md-shimmer { animation: md-shimmer 2.6s ease-in-out infinite; }

.md-status-pulse {
  position: relative;
}
.md-status-pulse::after {
  content: '';
  position: absolute;
  inset: -4px;
  border-radius: 999px;
  border: 1.5px solid currentColor;
  animation: md-pulse-ring 2.2s ease-out infinite;
  pointer-events: none;
}

/* ========================================================
   SCROLLBAR
   ======================================================== */
.md-scroll::-webkit-scrollbar { width: 10px; height: 10px; }
.md-scroll::-webkit-scrollbar-track { background: transparent; }
.md-scroll::-webkit-scrollbar-thumb { background: rgba(28, 31, 26, 0.08); border-radius: 999px; }
.md-scroll::-webkit-scrollbar-thumb:hover { background: rgba(28, 31, 26, 0.16); }

/* ========================================================
   GRID / SPACING
   ======================================================== */
.md-grid { display: grid; gap: 16px; }
.md-grid-12 { grid-template-columns: repeat(12, minmax(0, 1fr)); }

/* ========================================================
   TABLE / ROW
   ======================================================== */
.md-row {
  display: grid;
  grid-template-columns: 32px 1fr auto auto auto;
  align-items: center;
  gap: 12px;
  padding: 11px 14px;
  border-bottom: 1px solid var(--md-border-2);
  transition: background .15s;
  font-size: 12.5px;
}
.md-row:last-child { border-bottom: none; }
.md-row:hover { background: var(--md-surface); }

/* ========================================================
   HELPERS
   ======================================================== */
.md-truncate { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; }
.md-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.md-clamp-3 { display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }

/* Recharts text override */
.recharts-text { font-family: 'Geist', system-ui, sans-serif !important; font-size: 11px !important; fill: #8A8B83 !important; }
.recharts-cartesian-axis-line, .recharts-cartesian-axis-tick-line { stroke: #E1DCCF !important; }
.recharts-cartesian-grid-horizontal line, .recharts-cartesian-grid-vertical line { stroke: #ECE7DA !important; stroke-dasharray: 3 5 !important; }
.recharts-default-tooltip {
  background: #FFFFFF !important;
  border: 1px solid #E1DCCF !important;
  border-radius: 10px !important;
  box-shadow: 0 10px 24px -14px rgba(28, 31, 26, 0.2) !important;
  padding: 10px 14px !important;
  font-family: 'Geist', system-ui, sans-serif !important;
}
.recharts-tooltip-label { color: #1C1F1A !important; font-weight: 500 !important; font-size: 12px !important; margin-bottom: 6px !important; }
.recharts-tooltip-item { font-family: 'Geist Mono', monospace !important; font-size: 11.5px !important; color: #54574F !important; }
`,

  'src/App.js': `import React from 'react';
import './index.css';
import CommandBar from './components/CommandBar';
import NavRail from './components/NavRail';
import SubHeader from './components/SubHeader';
import ExecutiveSummary from './components/ExecutiveSummary';
import AnalyticsRow from './components/AnalyticsRow';
import OperationsRow from './components/OperationsRow';
import FinancialRow from './components/FinancialRow';
import TeamRow from './components/TeamRow';
import PipelineRow from './components/PipelineRow';
import ActivityTimeline from './components/ActivityTimeline';
import DashboardFooter from './components/DashboardFooter';
import AIRail from './components/AIRail';

export default function App() {
  return (
    <div className="md-shell">
      <CommandBar />
      <NavRail />
      <main className="md-content md-scroll">
        <SubHeader />
        <div className="px-6 pb-10 md:px-8">
          <ExecutiveSummary />
          <AnalyticsRow />
          <OperationsRow />
          <FinancialRow />
          <TeamRow />
          <PipelineRow />
          <ActivityTimeline />
          <DashboardFooter />
        </div>
      </main>
      <AIRail />
    </div>
  );
}
`,

  'src/data/meridian.js': `/** MERIDIAN — enterprise operations mock data */

export const WORKSPACES = [
  { id: 'ops',     name: 'Operations',  initials: 'OP' },
  { id: 'finance', name: 'Finance',     initials: 'FN' },
  { id: 'people',  name: 'People',      initials: 'PE' },
];

export const NAV_GROUPS = [
  {
    label: 'Operations',
    items: [
      { id: 'overview',   label: 'Overview',    icon: 'grid',     active: true,  badge: '' },
      { id: 'perf',       label: 'Performance', icon: 'spark',    active: false, badge: '12' },
      { id: 'sla',        label: 'SLA Health',  icon: 'shield',   active: false, badge: '' },
      { id: 'workflows',  label: 'Workflows',   icon: 'flow',     active: false, badge: '4' },
    ],
  },
  {
    label: 'Financial',
    items: [
      { id: 'revenue',  label: 'Revenue',     icon: 'trend',     active: false, badge: '' },
      { id: 'spending', label: 'Spending',    icon: 'pie',       active: false, badge: '' },
      { id: 'forecast', label: 'Forecast',    icon: 'compass',   active: false, badge: 'BETA' },
    ],
  },
  {
    label: 'People',
    items: [
      { id: 'teams',      label: 'Teams',         icon: 'users',  active: false, badge: '7' },
      { id: 'roles',      label: 'Roles',         icon: 'badge',  active: false, badge: '' },
      { id: 'permissions',label: 'Permissions',   icon: 'lock',   active: false, badge: '' },
    ],
  },
  {
    label: 'Tools',
    items: [
      { id: 'integrations', label: 'Integrations', icon: 'plug',  active: false, badge: '' },
      { id: 'audit',        label: 'Audit log',    icon: 'file',  active: false, badge: '' },
      { id: 'api',          label: 'API keys',     icon: 'key',   active: false, badge: '' },
    ],
  },
];

export const PAGE_ACTIONS = ['Customize', 'Export', 'Share'];

export const EXEC_HERO = {
  primary: {
    label: 'Operations index',
    value: '94.7',
    delta: '+2.4',
    period: 'vs. last 30 days',
    tone: 'emerald',
    sparkline: [88, 90, 89, 91, 89, 92, 93, 91, 94, 92, 94, 95],
  },
  secondary: [
    { label: 'Net new revenue', value: '$4.82M', delta: '+12.6%', tone: 'emerald' },
    { label: 'Open incidents',  value: '11',     delta: '-3',     tone: 'slate' },
    { label: 'Avg. cycle time', value: '38 min', delta: '-4 min', tone: 'emerald' },
    { label: 'Workspace usage', value: '78%',    delta: '+5pp',   tone: 'copper' },
  ],
};

/** Revenue & spending — 12 months for the analytics area chart */
export const REVENUE_SERIES = [
  { month: 'Jul', revenue: 3.2, spend: 1.9, net: 1.3 },
  { month: 'Aug', revenue: 3.4, spend: 2.0, net: 1.4 },
  { month: 'Sep', revenue: 3.6, spend: 2.1, net: 1.5 },
  { month: 'Oct', revenue: 3.5, spend: 2.0, net: 1.5 },
  { month: 'Nov', revenue: 3.8, spend: 2.2, net: 1.6 },
  { month: 'Dec', revenue: 4.0, spend: 2.3, net: 1.7 },
  { month: 'Jan', revenue: 3.9, spend: 2.4, net: 1.5 },
  { month: 'Feb', revenue: 4.1, spend: 2.5, net: 1.6 },
  { month: 'Mar', revenue: 4.4, spend: 2.6, net: 1.8 },
  { month: 'Apr', revenue: 4.6, spend: 2.6, net: 2.0 },
  { month: 'May', revenue: 4.7, spend: 2.7, net: 2.0 },
  { month: 'Jun', revenue: 4.82, spend: 2.78, net: 2.04 },
];

export const REVENUE_SEGMENTS = [
  { id: 'enterprise', label: 'Enterprise',     value: 2.41, share: 50, tone: 'emerald' },
  { id: 'business',   label: 'Business',       value: 1.31, share: 27, tone: 'slate' },
  { id: 'team',       label: 'Team',           value: 0.74, share: 15, tone: 'copper' },
  { id: 'public',     label: 'Public sector',  value: 0.36, share: 8,  tone: 'olive' },
];

export const TOP_ACCOUNTS = [
  { name: 'Northrange Holdings',  city: 'Geneva',   value: '$612K', delta: '+18.4%', tone: 'emerald' },
  { name: 'Halton Industries',    city: 'Singapore',value: '$528K', delta: '+9.1%',  tone: 'emerald' },
  { name: 'Westmark Logistics',   city: 'Rotterdam',value: '$487K', delta: '+4.6%',  tone: 'slate' },
  { name: 'Calder Aerospace',     city: 'Toulouse', value: '$402K', delta: '-2.3%',  tone: 'copper' },
];

/** Operations gauge — radial bar data */
export const OPS_GAUGES = [
  { label: 'Uptime',     value: 99.96, target: 99.5, tone: 'emerald', unit: '%' },
  { label: 'SLA met',    value: 97.4,  target: 95.0, tone: 'slate',   unit: '%' },
  { label: 'Resolution', value: 88.2,  target: 92.0, tone: 'copper',  unit: '%' },
];

export const WORKFLOWS = [
  { id: 'wf1', name: 'Procurement approvals',  owner: 'A. Calder',   stages: 'Stage 3 of 4', state: 'on-track',  pct: 76, tone: 'emerald' },
  { id: 'wf2', name: 'Vendor onboarding · APAC', owner: 'H. Murata',  stages: 'Stage 2 of 5', state: 'at-risk',   pct: 41, tone: 'copper'  },
  { id: 'wf3', name: 'Quarterly close · Q2',   owner: 'L. Beaumont', stages: 'Stage 4 of 6', state: 'on-track',  pct: 68, tone: 'emerald' },
  { id: 'wf4', name: 'Compliance audit · SOC2', owner: 'R. Okafor',  stages: 'Stage 1 of 3', state: 'attention', pct: 22, tone: 'slate'   },
];

/** Financial — donut for cost breakdown */
export const COST_BREAKDOWN = [
  { name: 'People',         value: 38, color: 'var(--md-emerald)' },
  { name: 'Infrastructure', value: 24, color: 'var(--md-slate)' },
  { name: 'Operations',     value: 18, color: 'var(--md-copper)' },
  { name: 'Marketing',      value: 12, color: 'var(--md-olive)' },
  { name: 'Misc',           value:  8, color: '#B5B4AA' },
];

export const FORECAST = [
  { quarter: 'Q3 \\u201924', projected: 12.4, actual: 12.1 },
  { quarter: 'Q4 \\u201924', projected: 13.1, actual: 13.4 },
  { quarter: 'Q1 \\u201925', projected: 13.9, actual: 13.7 },
  { quarter: 'Q2 \\u201925', projected: 14.6, actual: 14.9 },
  { quarter: 'Q3 \\u201925', projected: 15.4, actual: 15.6 },
  { quarter: 'Q4 \\u201925', projected: 16.1, actual: 16.0 },
  { quarter: 'Q1 \\u201926', projected: 16.9, actual: 17.2 },
  { quarter: 'Q2 \\u201926', projected: 17.8, actual: 18.1 },
];

/** Team and workspace */
export const DEPARTMENT_HEADS = [
  { name: 'Lena Beaumont',  role: 'Head of Finance',     dept: 'Finance',     status: 'In office',  tone: 'emerald', focus: 'Closing Q2 books · Day 6 of 10' },
  { name: 'Hiroshi Murata', role: 'VP Operations · APAC',dept: 'Operations',  status: 'In Tokyo',   tone: 'slate',   focus: 'Onboarding 14 new vendors' },
  { name: 'Rafael Okafor',  role: 'Chief Compliance',    dept: 'Risk',        status: 'Off-site',   tone: 'copper',  focus: 'SOC2 stage one prep'  },
  { name: 'Iris Vance',     role: 'Head of People',      dept: 'People',      status: 'In office',  tone: 'olive',   focus: 'Leveling review · 23 promos' },
];

export const WORKSPACE_UPDATES = [
  { who: 'Finance',      text: 'Q2 forecast model published \\u00B7 v2.3',         when: '8 min ago' },
  { who: 'Operations',   text: 'Run-book \\"Vendor onboarding\\" version published', when: '34 min ago' },
  { who: 'Engineering',  text: 'Cloud spend report routed to leadership',         when: '2 h ago' },
  { who: 'People',       text: 'Career framework v4 \\u2014 final read open',            when: '6 h ago' },
];

/** Smart task operations — process pipeline (NOT kanban) */
export const PIPELINE_STAGES = [
  { id: 'intake',   label: 'Intake',       count: 124, owner: 'Routing service', tone: 'slate' },
  { id: 'review',   label: 'Under review', count: 87,  owner: 'Operations',      tone: 'slate' },
  { id: 'approve',  label: 'Approval',     count: 41,  owner: 'Finance',         tone: 'copper' },
  { id: 'execute',  label: 'Execution',    count: 28,  owner: 'Workspace teams', tone: 'emerald' },
  { id: 'verify',   label: 'Verification', count: 11,  owner: 'Compliance',      tone: 'emerald' },
  { id: 'archive',  label: 'Archived',     count: 412, owner: 'Records',         tone: 'olive' },
];

export const PIPELINE_THROUGHPUT = [
  { day: 'Mon', value: 38 },
  { day: 'Tue', value: 42 },
  { day: 'Wed', value: 36 },
  { day: 'Thu', value: 51 },
  { day: 'Fri', value: 47 },
  { day: 'Sat', value: 14 },
  { day: 'Sun', value: 9 },
];

/** Activity timeline events */
export const ACTIVITY_EVENTS = [
  { time: '04:12 PM', who: 'L. Beaumont',  what: 'approved expense run',  detail: 'Run \\u00B7 EU-2026-Q2-04 \\u00B7 $182,400', tone: 'emerald' },
  { time: '03:54 PM', who: 'System',       what: 'reconciled bank ledger',detail: '14,802 transactions \\u00B7 $0 variance',   tone: 'slate'   },
  { time: '03:28 PM', who: 'H. Murata',    what: 'opened workflow',       detail: 'Vendor onboarding \\u00B7 APAC \\u00B7 stage 2',     tone: 'copper'  },
  { time: '02:11 PM', who: 'R. Okafor',    what: 'flagged document',      detail: 'Master services agreement \\u00B7 Calder \\u00B7 review', tone: 'copper'  },
  { time: '01:47 PM', who: 'I. Vance',     what: 'submitted policy v4',   detail: 'Career framework \\u00B7 23 promotions queued',    tone: 'olive'   },
  { time: '12:33 PM', who: 'System',       what: 'archived records',      detail: '218 closed tickets \\u00B7 SLA met',                tone: 'slate'   },
  { time: '11:09 AM', who: 'A. Calder',    what: 'closed incident',       detail: 'Incident INC-2026-3147 \\u00B7 P3 \\u00B7 resolved',        tone: 'emerald' },
];

/** AI insights for the right rail */
export const AI_INSIGHTS = [
  {
    id: 'ai-1',
    title: 'Spending in EU operations is trending 6.4% above forecast',
    detail: 'Three vendors in Germany are driving variance. Consider revising the Q3 envelope.',
    confidence: 92,
    action: 'Review variance',
    tone: 'copper',
  },
  {
    id: 'ai-2',
    title: 'Resolution time slipped under target for 3 days running',
    detail: 'L3 escalations from the Singapore desk are the leading contributor. Capacity is 84%.',
    confidence: 81,
    action: 'Open desk',
    tone: 'slate',
  },
  {
    id: 'ai-3',
    title: 'Q2 close is on track to finish 2 days ahead of schedule',
    detail: 'All five subsidiaries are reconciled. The Geneva office completed early.',
    confidence: 96,
    action: 'View close',
    tone: 'emerald',
  },
  {
    id: 'ai-4',
    title: 'Three new vendors in APAC are eligible for the fast-track program',
    detail: 'Risk score under 18, contract value under \\u20AC 250K, established under 12 months.',
    confidence: 88,
    action: 'Promote',
    tone: 'emerald',
  },
];

export const LIVE_TICKER = [
  { type: 'ops',     text: 'EU-2026-Q2-04 expense run approved',          when: 'just now',   tone: 'emerald' },
  { type: 'ops',     text: 'Bank ledger reconciled \\u00B7 $0 variance',         when: '18 min',     tone: 'slate'   },
  { type: 'risk',    text: 'Master services agreement flagged for review', when: '32 min',     tone: 'copper'  },
  { type: 'people',  text: '23 promotions queued for review',              when: '47 min',     tone: 'olive'   },
];

export const SYSTEM_STATUS = [
  { label: 'Operational',   value: 'All systems',     tone: 'emerald' },
  { label: 'Region',        value: 'EU \\u00B7 West \\u00B7 prod' },
  { label: 'Build',         value: 'v 4.18.2' },
  { label: 'Updated',       value: '2 min ago' },
];
`,

  'src/components/Icon.js': `import React from 'react';

/** Minimal enterprise icon set — uniform 1.4px strokes, geometric. */
export default function Icon({ name, size = 14, stroke = 1.4, className = '' }) {
  const p = {
    width: size, height: size, viewBox: '0 0 24 24', fill: 'none',
    stroke: 'currentColor', strokeWidth: stroke,
    strokeLinecap: 'round', strokeLinejoin: 'round',
    className,
  };
  switch (name) {
    case 'grid':     return <svg {...p}><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></svg>;
    case 'spark':    return <svg {...p}><path d="M3 17l5-6 4 4 5-7 4 5" /></svg>;
    case 'shield':   return <svg {...p}><path d="M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6l-8-3z" /></svg>;
    case 'flow':     return <svg {...p}><circle cx="6" cy="6" r="2.5" /><circle cx="18" cy="6" r="2.5" /><circle cx="12" cy="18" r="2.5" /><path d="M6 8.5v3a3 3 0 0 0 3 3h6a3 3 0 0 0 3-3v-3" /></svg>;
    case 'trend':    return <svg {...p}><path d="M3 17l6-6 4 4 8-8" /><path d="M14 7h7v7" /></svg>;
    case 'pie':      return <svg {...p}><path d="M12 3a9 9 0 0 1 9 9h-9z" /><path d="M12 21a9 9 0 1 1 9-9" /></svg>;
    case 'compass':  return <svg {...p}><circle cx="12" cy="12" r="9" /><path d="m15.5 8.5-2.5 7-7 2.5 2.5-7 7-2.5z" /></svg>;
    case 'users':    return <svg {...p}><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20a6.5 6.5 0 0 1 13 0" /><circle cx="17" cy="9" r="3" /><path d="M17 14c3 0 4.5 2 4.5 5" /></svg>;
    case 'badge':    return <svg {...p}><rect x="4" y="3" width="16" height="18" rx="2" /><circle cx="12" cy="11" r="3" /><path d="M9 17h6" /></svg>;
    case 'lock':     return <svg {...p}><rect x="4" y="10" width="16" height="11" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></svg>;
    case 'plug':     return <svg {...p}><path d="M9 7V3M15 7V3M5 7h14v5a7 7 0 0 1-14 0V7zM12 18v3" /></svg>;
    case 'file':     return <svg {...p}><path d="M14 3H6v18h12V7l-4-4z" /><path d="M14 3v4h4" /></svg>;
    case 'key':      return <svg {...p}><circle cx="7" cy="13" r="4" /><path d="M11 13h10M17 9v8M21 13l-2 2" /></svg>;
    case 'search':   return <svg {...p}><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>;
    case 'bell':     return <svg {...p}><path d="M5 17h14l-2-2V11a5 5 0 0 0-10 0v4l-2 2z" /><path d="M10 21a2 2 0 0 0 4 0" /></svg>;
    case 'cmd':      return <svg {...p}><path d="M7 9V7a2 2 0 1 1 2 2H7zm0 6v2a2 2 0 1 0 2-2H7zm10-6V7a2 2 0 1 0-2 2h2zm0 6v2a2 2 0 1 1-2-2h2zM7 9h10v6H7z" /></svg>;
    case 'plus':     return <svg {...p}><path d="M12 5v14M5 12h14" /></svg>;
    case 'chev-r':   return <svg {...p}><path d="m9 6 6 6-6 6" /></svg>;
    case 'chev-d':   return <svg {...p}><path d="m6 9 6 6 6-6" /></svg>;
    case 'check':    return <svg {...p}><path d="M5 13l4 4L19 7" /></svg>;
    case 'arrow':    return <svg {...p}><path d="M5 12h14M13 6l6 6-6 6" /></svg>;
    case 'arrow-up-r':return <svg {...p}><path d="M7 17 17 7M9 7h8v8" /></svg>;
    case 'sparkles':  return <svg {...p}><path d="M5 12 L 6 9 L 9 8 L 6 7 L 5 4 L 4 7 L 1 8 L 4 9 z" /><path d="M16 18 L 17 16 L 19 15 L 17 14 L 16 12 L 15 14 L 13 15 L 15 16 z" /><path d="M17 5 L 17.5 4 L 18.5 3.5 L 17.5 3 L 17 2 L 16.5 3 L 15.5 3.5 L 16.5 4 z" /></svg>;
    case 'globe':     return <svg {...p}><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a13 13 0 0 1 0 18M12 3a13 13 0 0 0 0 18" /></svg>;
    case 'clock':     return <svg {...p}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>;
    case 'export':    return <svg {...p}><path d="M12 3v12M7 8l5-5 5 5" /><path d="M5 16v3a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-3" /></svg>;
    case 'share':     return <svg {...p}><circle cx="6" cy="12" r="2.5" /><circle cx="18" cy="6" r="2.5" /><circle cx="18" cy="18" r="2.5" /><path d="M8 11l8-4M8 13l8 4" /></svg>;
    case 'dot':       return <svg {...p}><circle cx="12" cy="12" r="3" fill="currentColor" stroke="none" /></svg>;
    case 'circle':    return <svg {...p}><circle cx="12" cy="12" r="9" /></svg>;
    case 'spark-line':return <svg {...p}><path d="M3 18l4-6 4 3 5-9 5 7" /></svg>;
    default: return null;
  }
}
`,

  'src/components/CommandBar.js': `import React from 'react';
import Icon from './Icon';
import { WORKSPACES } from '../data/meridian';

export default function CommandBar() {
  return (
    <header
      className="md-cmd-bar md-fade"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        padding: '0 18px',
        background: 'rgba(251, 249, 243, 0.78)',
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
        borderBottom: '1px solid var(--md-border)',
      }}
    >
      {/* Left — brand + workspace switcher */}
      <div className="flex items-center" style={{ gap: 14, minWidth: 0 }}>
        <div className="flex items-center" style={{ gap: 10 }}>
          <span
            style={{
              width: 26, height: 26, borderRadius: 7,
              background: 'linear-gradient(155deg, #1F221C 0%, #3A3D34 100%)',
              color: '#ECE9DD',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 11, fontWeight: 600, letterSpacing: '0.06em',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.12)',
            }}
          >
            M
          </span>
          <span style={{ fontSize: 14, fontWeight: 600, letterSpacing: '0.04em' }}>MERIDIAN</span>
          <span className="md-mono" style={{ fontSize: 10.5, color: 'var(--md-text-3)', letterSpacing: '0.06em' }}>OPS / v4.18</span>
        </div>

        <span className="md-divider-v" style={{ height: 24, margin: '0 4px' }} />

        <button type="button" className="md-btn md-btn-ghost" style={{ padding: '0 10px' }}>
          <span
            style={{
              width: 22, height: 22, borderRadius: 6,
              background: 'var(--md-emerald-soft)',
              color: 'var(--md-emerald)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 9.5, fontWeight: 600, letterSpacing: '0.06em',
            }}
          >
            {WORKSPACES[0].initials}
          </span>
          <span style={{ fontSize: 12.5, fontWeight: 500 }}>{WORKSPACES[0].name} workspace</span>
          <Icon name="chev-d" size={12} className="opacity-60" />
        </button>
      </div>

      {/* Center — command palette */}
      <div className="flex-1" style={{ maxWidth: 480, margin: '0 12px' }}>
        <label
          className="flex items-center"
          style={{
            gap: 10,
            height: 32,
            padding: '0 12px',
            borderRadius: 9,
            background: 'var(--md-card)',
            border: '1px solid var(--md-border)',
            transition: 'border-color .2s',
          }}
        >
          <Icon name="search" size={13} className="opacity-60" />
          <input
            type="text"
            placeholder="Search across MERIDIAN — accounts, runs, workflows, people\\u2026"
            style={{
              flex: 1, minWidth: 0,
              border: 'none', background: 'transparent', outline: 'none',
              fontSize: 12.5, color: 'var(--md-text)',
            }}
          />
          <span
            className="md-mono"
            style={{
              fontSize: 10, color: 'var(--md-text-3)',
              padding: '2px 6px',
              border: '1px solid var(--md-border)',
              borderRadius: 5,
              background: 'var(--md-subtle)',
              letterSpacing: '0.04em',
            }}
          >
            \\u2318 K
          </span>
        </label>
      </div>

      {/* Right — quick actions + alerts + profile */}
      <div className="flex items-center" style={{ gap: 8 }}>
        <button type="button" className="md-btn">
          <Icon name="plus" size={12} />
          <span style={{ fontWeight: 500 }}>New</span>
        </button>
        <button type="button" className="md-btn md-btn-ghost" style={{ padding: '0 8px' }} title="Notifications">
          <span style={{ position: 'relative', display: 'inline-flex' }}>
            <Icon name="bell" size={14} />
            <span
              style={{
                position: 'absolute', top: -2, right: -2,
                width: 6, height: 6, borderRadius: 999,
                background: 'var(--md-copper)',
              }}
            />
          </span>
        </button>
        <span className="md-divider-v" style={{ height: 24, margin: '0 4px' }} />
        <div className="flex items-center" style={{ gap: 8 }}>
          <span
            style={{
              width: 28, height: 28, borderRadius: 999,
              background: 'linear-gradient(135deg, #4A6B5C 0%, #6E8A7C 100%)',
              color: '#ECE9DD',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 11, fontWeight: 600, letterSpacing: '0.04em',
            }}
          >
            LB
          </span>
          <div className="flex flex-col" style={{ lineHeight: 1.15 }}>
            <span style={{ fontSize: 12, fontWeight: 500 }}>Lena Beaumont</span>
            <span className="md-mono" style={{ fontSize: 10, color: 'var(--md-text-3)' }}>head of finance</span>
          </div>
        </div>
      </div>
    </header>
  );
}
`,

  'src/components/NavRail.js': `import React from 'react';
import Icon from './Icon';
import { NAV_GROUPS } from '../data/meridian';

export default function NavRail() {
  return (
    <aside
      className="md-nav-rail md-scroll"
      style={{
        background: 'var(--md-surface)',
        borderRight: '1px solid var(--md-border)',
        padding: '14px 12px',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 18,
      }}
    >
      {NAV_GROUPS.map((group) => (
        <div key={group.label}>
          <p
            style={{
              fontSize: 10,
              fontWeight: 500,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: 'var(--md-text-3)',
              padding: '0 11px',
              marginBottom: 7,
            }}
          >
            {group.label}
          </p>
          <div className="flex flex-col" style={{ gap: 2 }}>
            {group.items.map((item) => (
              <a
                key={item.id}
                href={'#' + item.id}
                className={'md-nav-item' + (item.active ? ' active' : '')}
              >
                <Icon name={item.icon} size={14} />
                <span style={{ fontWeight: item.active ? 500 : 400 }}>{item.label}</span>
                {item.badge && <span className="md-nav-badge">{item.badge}</span>}
              </a>
            ))}
          </div>
        </div>
      ))}

      <div style={{ marginTop: 'auto' }}>
        <div
          style={{
            border: '1px solid var(--md-border)',
            borderRadius: 11,
            padding: '11px 12px',
            background: 'var(--md-card)',
            fontSize: 11.5,
            color: 'var(--md-text-2)',
          }}
        >
          <div className="flex items-center" style={{ gap: 7, marginBottom: 4 }}>
            <span style={{ position: 'relative', display: 'inline-flex' }}>
              <span style={{ width: 7, height: 7, borderRadius: 999, background: 'var(--md-emerald)', display: 'inline-block' }} />
            </span>
            <span style={{ fontWeight: 500, color: 'var(--md-text)' }}>All systems operational</span>
          </div>
          <p className="md-mono" style={{ fontSize: 10, color: 'var(--md-text-3)', letterSpacing: '0.04em' }}>
            EU \\u00B7 West \\u00B7 prod \\u00B7 v4.18.2
          </p>
        </div>
      </div>
    </aside>
  );
}
`,

  'src/components/SubHeader.js': `import React from 'react';
import Icon from './Icon';
import { PAGE_ACTIONS } from '../data/meridian';

export default function SubHeader() {
  return (
    <div
      className="md-fade"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        padding: '20px 32px 14px',
        borderBottom: '1px solid var(--md-border-2)',
        background: 'transparent',
        flexWrap: 'wrap',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div className="flex items-center" style={{ gap: 6, fontSize: 11, color: 'var(--md-text-3)' }}>
          <span>Operations</span>
          <Icon name="chev-r" size={10} />
          <span style={{ color: 'var(--md-text)' }}>Executive overview</span>
          <span className="md-chip md-chip-emerald" style={{ marginLeft: 8, height: 18, padding: '0 7px', fontSize: 9.5 }}>
            <span className="md-dot" /> Live
          </span>
        </div>
        <h1 style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.012em', marginTop: 5 }}>
          Operations console
          <span style={{ fontWeight: 300, color: 'var(--md-text-3)', marginLeft: 10 }}>\\u00B7 Q2 2026</span>
        </h1>
      </div>

      <div className="flex items-center flex-wrap" style={{ gap: 8 }}>
        <button type="button" className="md-btn">
          <Icon name="globe" size={12} />
          <span>All regions</span>
          <Icon name="chev-d" size={11} className="opacity-60" />
        </button>
        <button type="button" className="md-btn">
          <Icon name="clock" size={12} />
          <span>This quarter</span>
          <Icon name="chev-d" size={11} className="opacity-60" />
        </button>
        <span className="md-divider-v" style={{ height: 22, margin: '0 4px' }} />
        {PAGE_ACTIONS.map((act, i) => (
          <button
            key={act}
            type="button"
            className={i === PAGE_ACTIONS.length - 1 ? 'md-btn md-btn-primary' : 'md-btn'}
          >
            {act === 'Export' && <Icon name="export" size={12} />}
            {act === 'Share' && <Icon name="share" size={12} />}
            <span>{act}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
`,

  'src/components/ExecutiveSummary.js': `import React from 'react';
import { EXEC_HERO } from '../data/meridian';

const TONE_COLOR = {
  emerald: 'var(--md-emerald)',
  slate:   'var(--md-slate)',
  copper:  'var(--md-copper)',
  olive:   'var(--md-olive)',
};

function Sparkline({ data, color }) {
  const W = 280, H = 60;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const stepX = W / (data.length - 1);
  const pts = data.map((v, i) => {
    const x = i * stepX;
    const y = H - ((v - min) / range) * (H - 8) - 4;
    return [x, y];
  });
  const d = pts.map((p, i) => (i === 0 ? 'M' : 'L') + ' ' + p[0].toFixed(2) + ' ' + p[1].toFixed(2)).join(' ');
  const area = d + ' L ' + W + ' ' + H + ' L 0 ' + H + ' Z';
  const last = pts[pts.length - 1];
  return (
    <svg viewBox={'0 0 ' + W + ' ' + H} preserveAspectRatio="none" style={{ width: '100%', height: H }}>
      <defs>
        <linearGradient id="md-spark-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.22" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#md-spark-grad)" />
      <path d={d} fill="none" stroke={color} strokeWidth="1.6" />
      <circle cx={last[0]} cy={last[1]} r="3.2" fill={color} stroke="#FFFFFF" strokeWidth="1.5" />
    </svg>
  );
}

export default function ExecutiveSummary() {
  const { primary, secondary } = EXEC_HERO;
  const primaryColor = TONE_COLOR[primary.tone];

  return (
    <section className="md-rise" style={{ marginTop: 22 }}>
      <div
        className="md-panel"
        style={{
          padding: 0,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1.45fr) minmax(0, 2fr)',
            gap: 0,
          }}
        >
          {/* Primary KPI */}
          <div style={{ padding: '26px 30px', borderRight: '1px solid var(--md-border-2)', minWidth: 0 }}>
            <p className="md-eyebrow">{primary.label}</p>
            <div className="flex items-baseline" style={{ gap: 14, marginTop: 8, flexWrap: 'wrap' }}>
              <h2 className="md-tnum" style={{ fontSize: 56, fontWeight: 500, lineHeight: 0.95, letterSpacing: '-0.025em' }}>
                {primary.value}
              </h2>
              <div className="flex items-center" style={{ gap: 8 }}>
                <span className="md-chip md-chip-emerald md-tnum" style={{ fontSize: 11 }}>
                  <span className="md-dot" /> {primary.delta}
                </span>
                <span style={{ fontSize: 11.5, color: 'var(--md-text-3)' }}>{primary.period}</span>
              </div>
            </div>

            <div style={{ marginTop: 18 }}>
              <Sparkline data={primary.sparkline} color={primaryColor} />
            </div>

            <div className="flex items-center" style={{ gap: 10, marginTop: 4 }}>
              <p className="md-mono" style={{ fontSize: 10, color: 'var(--md-text-3)' }}>
                JUL \\u2014 JUN \\u00B7 12 MONTH WINDOW
              </p>
              <span className="md-chip" style={{ height: 18, padding: '0 7px', fontSize: 9.5 }}>Target 92.0</span>
            </div>
          </div>

          {/* Secondary metrics */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              padding: 12,
              gap: 0,
            }}
          >
            {secondary.map((m, i) => {
              const c = TONE_COLOR[m.tone] || TONE_COLOR.slate;
              const isRowStart = i % 2 === 0;
              const isFirstRow  = i < 2;
              return (
                <div
                  key={m.label}
                  style={{
                    padding: '14px 18px',
                    borderRight: isRowStart ? '1px solid var(--md-border-2)' : 'none',
                    borderBottom: isFirstRow ? '1px solid var(--md-border-2)' : 'none',
                    minWidth: 0,
                  }}
                >
                  <div className="flex items-center" style={{ gap: 8 }}>
                    <span
                      style={{
                        width: 6, height: 6, borderRadius: 999,
                        background: c,
                      }}
                    />
                    <p className="md-label md-truncate" style={{ fontSize: 11 }}>{m.label}</p>
                  </div>
                  <p className="md-tnum" style={{ fontSize: 28, fontWeight: 500, marginTop: 8, letterSpacing: '-0.02em' }}>{m.value}</p>
                  <p className="md-tnum" style={{ fontSize: 11, color: c, marginTop: 3 }}>{m.delta}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
`,

  'src/components/AnalyticsRow.js': `import React from 'react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import Icon from './Icon';
import { REVENUE_SERIES, REVENUE_SEGMENTS, TOP_ACCOUNTS } from '../data/meridian';

const TONE_HEX = {
  emerald: '#4A6B5C',
  slate:   '#5B6B7D',
  copper:  '#A6755A',
  olive:   '#7A7B47',
};

export default function AnalyticsRow() {
  return (
    <section className="md-grid md-grid-12 md-rise" style={{ marginTop: 16 }}>
      {/* Revenue chart */}
      <div className="md-panel" style={{ gridColumn: 'span 8', padding: '18px 20px', minWidth: 0 }}>
        <div className="flex items-start justify-between" style={{ marginBottom: 10, gap: 12, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0 }}>
            <p className="md-eyebrow">Executive analytics</p>
            <h3 style={{ fontSize: 16, fontWeight: 500, marginTop: 4, letterSpacing: '-0.005em' }}>
              Net new revenue \\u00B7 last twelve months
            </h3>
          </div>
          <div className="flex items-center" style={{ gap: 6, flexWrap: 'wrap' }}>
            {[
              { label: 'Revenue',  color: TONE_HEX.emerald },
              { label: 'Spending', color: TONE_HEX.copper },
              { label: 'Net',      color: TONE_HEX.slate },
            ].map((s) => (
              <span key={s.label} className="flex items-center" style={{ gap: 5, fontSize: 11, color: 'var(--md-text-2)' }}>
                <span style={{ width: 8, height: 8, borderRadius: 999, background: s.color }} />
                {s.label}
              </span>
            ))}
            <span className="md-divider-v" style={{ height: 18, margin: '0 4px' }} />
            <div className="flex" style={{ gap: 4 }}>
              {['1M', '3M', '6M', '12M'].map((p, i) => (
                <button
                  key={p}
                  type="button"
                  className={i === 3 ? 'md-btn md-btn-primary' : 'md-btn'}
                  style={{ height: 24, padding: '0 9px', fontSize: 11 }}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div style={{ height: 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={REVENUE_SERIES} margin={{ top: 6, right: 4, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="md-rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"   stopColor={TONE_HEX.emerald} stopOpacity={0.30} />
                  <stop offset="100%" stopColor={TONE_HEX.emerald} stopOpacity={0}    />
                </linearGradient>
                <linearGradient id="md-spend" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"   stopColor={TONE_HEX.copper} stopOpacity={0.18} />
                  <stop offset="100%" stopColor={TONE_HEX.copper} stopOpacity={0}    />
                </linearGradient>
                <linearGradient id="md-net" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"   stopColor={TONE_HEX.slate} stopOpacity={0.18} />
                  <stop offset="100%" stopColor={TONE_HEX.slate} stopOpacity={0}    />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} />
              <XAxis dataKey="month" tickLine={false} axisLine={false} dy={4} />
              <YAxis tickLine={false} axisLine={false} dx={-4} tickFormatter={(v) => '$' + v + 'M'} />
              <Tooltip
                formatter={(v) => '$' + Number(v).toFixed(2) + 'M'}
                cursor={{ stroke: '#E1DCCF', strokeDasharray: '3 4' }}
              />
              <Area type="monotone" dataKey="revenue" stroke={TONE_HEX.emerald} strokeWidth={1.8} fill="url(#md-rev)" />
              <Area type="monotone" dataKey="spend"   stroke={TONE_HEX.copper}  strokeWidth={1.4} fill="url(#md-spend)" />
              <Area type="monotone" dataKey="net"     stroke={TONE_HEX.slate}   strokeWidth={1.4} strokeDasharray="4 4" fill="url(#md-net)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Revenue segments + top accounts */}
      <div className="md-panel" style={{ gridColumn: 'span 4', padding: '18px 20px', minWidth: 0 }}>
        <p className="md-eyebrow">Revenue by segment</p>
        <h3 style={{ fontSize: 15, fontWeight: 500, marginTop: 4 }}>Segment breakdown</h3>

        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {REVENUE_SEGMENTS.map((s) => (
            <div key={s.id}>
              <div className="flex items-center justify-between" style={{ marginBottom: 5, gap: 8 }}>
                <span style={{ fontSize: 12, color: 'var(--md-text-2)' }}>{s.label}</span>
                <span className="md-tnum md-mono" style={{ fontSize: 11, color: 'var(--md-text)' }}>
                  \${s.value.toFixed(2)}M
                  <span style={{ color: 'var(--md-text-3)', marginLeft: 6 }}>{s.share}%</span>
                </span>
              </div>
              <div style={{ height: 4, background: 'var(--md-subtle)', borderRadius: 999, overflow: 'hidden' }}>
                <div
                  style={{
                    height: '100%',
                    width: s.share + '%',
                    background: TONE_HEX[s.tone],
                    borderRadius: 999,
                  }}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="md-divider" style={{ margin: '18px 0 14px' }} />

        <p className="md-eyebrow">Top accounts</p>
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {TOP_ACCOUNTS.map((a) => (
            <div key={a.name} className="flex items-center justify-between" style={{ padding: '6px 0', gap: 8 }}>
              <div style={{ minWidth: 0 }}>
                <p className="md-truncate" style={{ fontSize: 12.5, fontWeight: 500 }}>{a.name}</p>
                <p style={{ fontSize: 10.5, color: 'var(--md-text-3)', marginTop: 1 }}>{a.city}</p>
              </div>
              <div className="text-right" style={{ flexShrink: 0 }}>
                <p className="md-tnum md-mono" style={{ fontSize: 11.5, color: 'var(--md-text)' }}>{a.value}</p>
                <p className="md-tnum md-mono" style={{ fontSize: 10, color: TONE_HEX[a.tone], marginTop: 1 }}>{a.delta}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
`,

  'src/components/OperationsRow.js': `import React from 'react';
import { ResponsiveContainer, RadialBarChart, RadialBar } from 'recharts';
import Icon from './Icon';
import { OPS_GAUGES, WORKFLOWS } from '../data/meridian';

const TONE_HEX = {
  emerald: '#4A6B5C',
  slate:   '#5B6B7D',
  copper:  '#A6755A',
  olive:   '#7A7B47',
};

function Gauge({ label, value, target, tone, unit }) {
  const color = TONE_HEX[tone] || TONE_HEX.slate;
  const data = [{ name: label, value, fill: color }];
  const onTarget = value >= target;
  return (
    <div style={{ position: 'relative', minWidth: 0 }}>
      <div style={{ position: 'relative', height: 130 }}>
        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart
            innerRadius="78%"
            outerRadius="100%"
            data={data}
            startAngle={210}
            endAngle={-30}
            barSize={9}
          >
            <RadialBar background={{ fill: 'var(--md-subtle)' }} dataKey="value" cornerRadius={6} domain={[0, 100]} />
          </RadialBarChart>
        </ResponsiveContainer>
        <div
          style={{
            position: 'absolute', inset: 0,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <span className="md-tnum" style={{ fontSize: 22, fontWeight: 500, letterSpacing: '-0.015em' }}>
            {value.toFixed(1)}<span style={{ fontSize: 12, color: 'var(--md-text-3)', marginLeft: 1 }}>{unit}</span>
          </span>
          <span style={{ fontSize: 10.5, color: 'var(--md-text-3)', marginTop: 2 }}>target {target}{unit}</span>
        </div>
      </div>
      <div className="flex items-center justify-between" style={{ marginTop: 6 }}>
        <p style={{ fontSize: 12.5, fontWeight: 500 }}>{label}</p>
        <span
          className="md-chip"
          style={{
            height: 18, padding: '0 7px', fontSize: 9.5,
            background: onTarget ? 'var(--md-emerald-soft)' : 'var(--md-copper-soft)',
            borderColor: onTarget ? 'var(--md-emerald-edge)' : 'var(--md-copper-edge)',
            color:       onTarget ? 'var(--md-emerald)'      : 'var(--md-copper)',
          }}
        >
          {onTarget ? 'On target' : 'Below'}
        </span>
      </div>
    </div>
  );
}

export default function OperationsRow() {
  return (
    <section className="md-grid md-grid-12 md-rise" style={{ marginTop: 16 }}>
      {/* Operations gauges */}
      <div className="md-panel" style={{ gridColumn: 'span 5', padding: '18px 22px', minWidth: 0 }}>
        <div className="flex items-center justify-between" style={{ marginBottom: 14, gap: 8 }}>
          <div>
            <p className="md-eyebrow">Business operations</p>
            <h3 style={{ fontSize: 15, fontWeight: 500, marginTop: 3 }}>Service health</h3>
          </div>
          <span className="md-chip">last 24h</span>
        </div>
        <div className="md-grid" style={{ gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 14 }}>
          {OPS_GAUGES.map((g) => (
            <Gauge key={g.label} {...g} />
          ))}
        </div>
      </div>

      {/* Workflows */}
      <div className="md-panel" style={{ gridColumn: 'span 7', padding: 0, minWidth: 0 }}>
        <div
          className="flex items-center justify-between"
          style={{ padding: '16px 20px', borderBottom: '1px solid var(--md-border-2)', gap: 8 }}
        >
          <div>
            <p className="md-eyebrow">Workflow throughput</p>
            <h3 style={{ fontSize: 15, fontWeight: 500, marginTop: 3 }}>Active operational runs</h3>
          </div>
          <button type="button" className="md-btn md-btn-ghost" style={{ fontSize: 11.5 }}>
            View all <Icon name="arrow" size={11} />
          </button>
        </div>

        <div>
          <div className="md-row" style={{ background: 'var(--md-surface)', padding: '8px 14px', borderBottom: '1px solid var(--md-border-2)' }}>
            <span className="md-label" style={{ gridColumn: '1 / span 2' }}>Workflow / owner</span>
            <span className="md-label">Stage</span>
            <span className="md-label">Progress</span>
            <span className="md-label">State</span>
          </div>
          {WORKFLOWS.map((w) => {
            const c = TONE_HEX[w.tone] || TONE_HEX.slate;
            return (
              <div key={w.id} className="md-row">
                <span
                  style={{
                    width: 26, height: 26, borderRadius: 7,
                    background: 'var(--md-subtle)',
                    color: 'var(--md-text)',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 10, fontWeight: 500, letterSpacing: '0.04em',
                  }}
                >
                  {w.id.replace('wf', '')}
                </span>
                <div style={{ minWidth: 0 }}>
                  <p className="md-truncate" style={{ fontSize: 12.5, fontWeight: 500 }}>{w.name}</p>
                  <p className="md-truncate" style={{ fontSize: 10.5, color: 'var(--md-text-3)', marginTop: 1 }}>{w.owner}</p>
                </div>
                <span className="md-mono" style={{ fontSize: 11, color: 'var(--md-text-2)' }}>{w.stages}</span>
                <div className="flex items-center" style={{ gap: 8, width: 110 }}>
                  <div style={{ flex: 1, height: 4, background: 'var(--md-subtle)', borderRadius: 999, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: w.pct + '%', background: c, borderRadius: 999 }} />
                  </div>
                  <span className="md-tnum md-mono" style={{ fontSize: 10.5, color: 'var(--md-text-2)', width: 28, textAlign: 'right' }}>{w.pct}%</span>
                </div>
                <span
                  className="md-chip"
                  style={{
                    height: 19, padding: '0 7px', fontSize: 9.5,
                    background: 'rgba(255,255,255,0.5)',
                    borderColor: c,
                    color: c,
                  }}
                >
                  <span className="md-dot" />
                  {w.state}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
`,

  'src/components/FinancialRow.js': `import React from 'react';
import { ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import Icon from './Icon';
import { COST_BREAKDOWN, FORECAST } from '../data/meridian';

const COST_HEX = ['#4A6B5C', '#5B6B7D', '#A6755A', '#7A7B47', '#B5B4AA'];

export default function FinancialRow() {
  const total = COST_BREAKDOWN.reduce((s, x) => s + x.value, 0);

  return (
    <section className="md-grid md-grid-12 md-rise" style={{ marginTop: 16 }}>
      {/* Cost breakdown — donut */}
      <div className="md-panel" style={{ gridColumn: 'span 4', padding: '18px 20px', minWidth: 0 }}>
        <p className="md-eyebrow">Financial insights</p>
        <h3 style={{ fontSize: 15, fontWeight: 500, marginTop: 4 }}>Spending allocation</h3>

        <div style={{ position: 'relative', height: 200, marginTop: 10 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={COST_BREAKDOWN}
                dataKey="value"
                cx="50%"
                cy="50%"
                innerRadius={56}
                outerRadius={88}
                paddingAngle={1.5}
                stroke="#FFFFFF"
                strokeWidth={2}
                isAnimationActive={false}
              >
                {COST_BREAKDOWN.map((c, i) => (
                  <Cell key={c.name} fill={COST_HEX[i % COST_HEX.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(v) => v + '%'} />
            </PieChart>
          </ResponsiveContainer>
          <div
            style={{
              position: 'absolute', inset: 0,
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <span className="md-tnum" style={{ fontSize: 24, fontWeight: 500, letterSpacing: '-0.02em' }}>$11.8M</span>
            <span style={{ fontSize: 10.5, color: 'var(--md-text-3)', marginTop: 2 }}>quarterly spend</span>
          </div>
        </div>

        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {COST_BREAKDOWN.map((c, i) => (
            <div key={c.name} className="flex items-center justify-between" style={{ gap: 8 }}>
              <span className="flex items-center" style={{ gap: 7, minWidth: 0 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: COST_HEX[i % COST_HEX.length] }} />
                <span className="md-truncate" style={{ fontSize: 12, color: 'var(--md-text-2)' }}>{c.name}</span>
              </span>
              <span className="md-tnum md-mono" style={{ fontSize: 11, color: 'var(--md-text)' }}>{c.value}%</span>
            </div>
          ))}
        </div>
      </div>

      {/* Forecast */}
      <div className="md-panel" style={{ gridColumn: 'span 5', padding: '18px 20px', minWidth: 0 }}>
        <div className="flex items-center justify-between" style={{ marginBottom: 8, gap: 8, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0 }}>
            <p className="md-eyebrow">Revenue forecast</p>
            <h3 style={{ fontSize: 15, fontWeight: 500, marginTop: 4 }}>Eight quarter trajectory</h3>
          </div>
          <span className="md-chip md-chip-emerald">
            <span className="md-dot" /> +5.8% vs plan
          </span>
        </div>

        <div style={{ height: 220, marginTop: 8 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={FORECAST} barGap={2} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <CartesianGrid vertical={false} />
              <XAxis dataKey="quarter" tickLine={false} axisLine={false} dy={4} />
              <YAxis tickLine={false} axisLine={false} tickFormatter={(v) => '$' + v + 'M'} />
              <Tooltip formatter={(v) => '$' + Number(v).toFixed(1) + 'M'} cursor={{ fill: 'rgba(28,31,26,0.04)' }} />
              <Bar dataKey="projected" fill="#E1DCCF" radius={[3, 3, 0, 0]} />
              <Bar dataKey="actual"    fill="#4A6B5C" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="flex items-center" style={{ gap: 16, marginTop: 4 }}>
          <span className="flex items-center" style={{ gap: 6, fontSize: 11, color: 'var(--md-text-2)' }}>
            <span style={{ width: 9, height: 9, borderRadius: 2, background: '#E1DCCF' }} /> Projected
          </span>
          <span className="flex items-center" style={{ gap: 6, fontSize: 11, color: 'var(--md-text-2)' }}>
            <span style={{ width: 9, height: 9, borderRadius: 2, background: '#4A6B5C' }} /> Actual
          </span>
        </div>
      </div>

      {/* Cash position card */}
      <div className="md-panel-deep" style={{ gridColumn: 'span 3', padding: '20px 22px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <p className="md-eyebrow" style={{ color: 'rgba(236, 233, 221, 0.6)' }}>Cash position</p>
        <h3 className="md-tnum" style={{ fontSize: 32, fontWeight: 500, letterSpacing: '-0.02em', color: '#ECE9DD' }}>$42.6M</h3>
        <div className="flex items-center" style={{ gap: 8 }}>
          <span
            className="md-chip"
            style={{
              background: 'rgba(126, 158, 138, 0.18)',
              borderColor: 'rgba(126, 158, 138, 0.4)',
              color: '#A8C3B3',
            }}
          >
            <span className="md-dot" /> +6.2%
          </span>
          <span style={{ fontSize: 11, color: 'rgba(236,233,221,0.6)' }}>vs. last quarter</span>
        </div>

        <div className="md-divider" style={{ background: 'rgba(255,255,255,0.08)', margin: '6px 0' }} />

        <div className="flex flex-col" style={{ gap: 8 }}>
          {[
            { label: 'Operating',  value: '$28.3M', share: 66 },
            { label: 'Reserves',   value: '$10.1M', share: 23 },
            { label: 'Strategic',  value: '$4.2M',  share: 11 },
          ].map((row) => (
            <div key={row.label}>
              <div className="flex items-center justify-between" style={{ fontSize: 11.5, color: 'rgba(236,233,221,0.7)' }}>
                <span>{row.label}</span>
                <span className="md-mono">{row.value}</span>
              </div>
              <div style={{ height: 3, marginTop: 5, background: 'rgba(255,255,255,0.08)', borderRadius: 999, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: row.share + '%', background: 'rgba(126,158,138,0.75)', borderRadius: 999 }} />
              </div>
            </div>
          ))}
        </div>

        <button type="button" className="md-btn" style={{ marginTop: 6, background: 'rgba(255,255,255,0.06)', color: '#ECE9DD', borderColor: 'rgba(255,255,255,0.12)' }}>
          Treasury report <Icon name="arrow" size={11} />
        </button>
      </div>
    </section>
  );
}
`,

  'src/components/TeamRow.js': `import React from 'react';
import Icon from './Icon';
import { DEPARTMENT_HEADS, WORKSPACE_UPDATES } from '../data/meridian';

const TONE_HEX = {
  emerald: '#4A6B5C',
  slate:   '#5B6B7D',
  copper:  '#A6755A',
  olive:   '#7A7B47',
};

const TONE_GRAD = {
  emerald: 'linear-gradient(135deg, #4A6B5C 0%, #6E8A7C 100%)',
  slate:   'linear-gradient(135deg, #5B6B7D 0%, #7E8C9C 100%)',
  copper:  'linear-gradient(135deg, #A6755A 0%, #C29178 100%)',
  olive:   'linear-gradient(135deg, #7A7B47 0%, #969866 100%)',
};

export default function TeamRow() {
  return (
    <section className="md-grid md-grid-12 md-rise" style={{ marginTop: 16 }}>
      {/* Department heads */}
      <div className="md-panel" style={{ gridColumn: 'span 8', padding: '18px 20px', minWidth: 0 }}>
        <div className="flex items-center justify-between" style={{ marginBottom: 14, gap: 8 }}>
          <div>
            <p className="md-eyebrow">Team \\u0026 workspace</p>
            <h3 style={{ fontSize: 15, fontWeight: 500, marginTop: 3 }}>Department heads on duty</h3>
          </div>
          <span className="md-chip md-chip-emerald">
            <span className="md-dot" /> 4 of 4 reporting
          </span>
        </div>

        <div className="md-grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
          {DEPARTMENT_HEADS.map((p) => {
            const initials = p.name.split(' ').map((s) => s[0]).slice(0, 2).join('');
            const tone = TONE_HEX[p.tone] || TONE_HEX.slate;
            return (
              <div
                key={p.name}
                className="md-panel-flat"
                style={{
                  padding: '12px 14px',
                  display: 'grid',
                  gridTemplateColumns: '40px 1fr auto',
                  gap: 12,
                  alignItems: 'center',
                  minWidth: 0,
                }}
              >
                <span
                  style={{
                    width: 40, height: 40, borderRadius: 9,
                    background: TONE_GRAD[p.tone] || TONE_GRAD.slate,
                    color: '#FBF9F3',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 13, fontWeight: 500, letterSpacing: '0.04em',
                  }}
                >
                  {initials}
                </span>
                <div style={{ minWidth: 0 }}>
                  <p className="md-truncate" style={{ fontSize: 12.5, fontWeight: 500 }}>{p.name}</p>
                  <p className="md-truncate" style={{ fontSize: 11, color: 'var(--md-text-3)', marginTop: 1 }}>{p.role}</p>
                  <p className="md-clamp-2" style={{ fontSize: 11.5, color: 'var(--md-text-2)', marginTop: 5 }}>{p.focus}</p>
                </div>
                <span
                  className="md-chip"
                  style={{
                    height: 19, padding: '0 7px', fontSize: 9.5,
                    background: 'rgba(255,255,255,0.6)',
                    borderColor: tone,
                    color: tone,
                  }}
                >
                  <span className="md-dot" /> {p.status}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Workspace updates */}
      <div className="md-panel" style={{ gridColumn: 'span 4', padding: '18px 20px', minWidth: 0 }}>
        <div className="flex items-center justify-between" style={{ marginBottom: 6, gap: 8 }}>
          <div>
            <p className="md-eyebrow">Workspace updates</p>
            <h3 style={{ fontSize: 15, fontWeight: 500, marginTop: 3 }}>Across departments</h3>
          </div>
          <button type="button" className="md-btn md-btn-ghost" style={{ fontSize: 11 }}>
            All <Icon name="arrow" size={10} />
          </button>
        </div>

        <ul style={{ marginTop: 8, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {WORKSPACE_UPDATES.map((u) => (
            <li
              key={u.text}
              className="md-panel-flat"
              style={{ padding: '10px 12px', display: 'flex', gap: 10, alignItems: 'flex-start' }}
            >
              <span
                style={{
                  marginTop: 4,
                  width: 6, height: 6, borderRadius: 999,
                  background: 'var(--md-graphite)',
                  flexShrink: 0,
                }}
              />
              <div style={{ minWidth: 0, flex: 1 }}>
                <p className="md-clamp-2" style={{ fontSize: 12.5, color: 'var(--md-text)' }}>{u.text}</p>
                <p className="md-mono" style={{ fontSize: 10, color: 'var(--md-text-3)', marginTop: 4, letterSpacing: '0.04em' }}>
                  {u.who.toUpperCase()} \\u00B7 {u.when}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
`,

  'src/components/PipelineRow.js': `import React from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, Tooltip } from 'recharts';
import Icon from './Icon';
import { PIPELINE_STAGES, PIPELINE_THROUGHPUT } from '../data/meridian';

const TONE_HEX = {
  emerald: '#4A6B5C',
  slate:   '#5B6B7D',
  copper:  '#A6755A',
  olive:   '#7A7B47',
};

export default function PipelineRow() {
  const total = PIPELINE_STAGES.reduce((s, x) => s + x.count, 0);

  return (
    <section className="md-rise" style={{ marginTop: 16 }}>
      <div className="md-panel" style={{ padding: 0, overflow: 'hidden' }}>
        <div
          className="flex items-start justify-between"
          style={{ padding: '18px 22px', borderBottom: '1px solid var(--md-border-2)', gap: 16, flexWrap: 'wrap' }}
        >
          <div>
            <p className="md-eyebrow">Smart task operations</p>
            <h3 style={{ fontSize: 15, fontWeight: 500, marginTop: 4 }}>
              Operational pipeline
              <span className="md-mono md-tnum" style={{ fontSize: 12, color: 'var(--md-text-3)', marginLeft: 10, letterSpacing: '0.04em' }}>
                {total.toLocaleString()} items in motion
              </span>
            </h3>
          </div>
          <div className="flex items-center" style={{ gap: 8 }}>
            <button type="button" className="md-btn">
              <Icon name="flow" size={12} />
              <span>Configure</span>
            </button>
            <button type="button" className="md-btn md-btn-primary">
              <Icon name="plus" size={12} />
              <span>New process</span>
            </button>
          </div>
        </div>

        {/* Pipeline strip */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(' + PIPELINE_STAGES.length + ', minmax(0,1fr))',
            gap: 8,
            padding: '18px 22px 6px',
          }}
        >
          {PIPELINE_STAGES.map((s, i) => {
            const c = TONE_HEX[s.tone] || TONE_HEX.slate;
            const max = Math.max(...PIPELINE_STAGES.map((x) => x.count));
            const h = 8 + (s.count / max) * 38;
            const isLast = i === PIPELINE_STAGES.length - 1;
            return (
              <div key={s.id} style={{ position: 'relative', minWidth: 0 }}>
                {/* Connector */}
                {!isLast && (
                  <span
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      top: 18, right: -8,
                      width: 16,
                      height: 1,
                      borderTop: '1.5px dashed var(--md-border)',
                      pointerEvents: 'none',
                    }}
                  />
                )}
                <div
                  className="md-panel-flat"
                  style={{
                    padding: '14px 14px 12px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    minWidth: 0,
                    minHeight: 138,
                    borderColor: 'var(--md-border-2)',
                  }}
                >
                  <div className="flex items-center" style={{ gap: 6 }}>
                    <span
                      className="md-mono"
                      style={{
                        width: 18, height: 18, borderRadius: 5,
                        background: 'var(--md-card)',
                        border: '1px solid var(--md-border)',
                        color: 'var(--md-text-2)',
                        fontSize: 9.5, fontWeight: 600,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      0{i + 1}
                    </span>
                    <p style={{ fontSize: 11.5, fontWeight: 500, color: 'var(--md-text)' }}>{s.label}</p>
                  </div>
                  <p className="md-tnum" style={{ fontSize: 26, fontWeight: 500, letterSpacing: '-0.02em', color: 'var(--md-text)' }}>
                    {s.count.toLocaleString()}
                  </p>
                  <div style={{ position: 'relative', height: 5, background: 'var(--md-subtle)', borderRadius: 999, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: ((s.count / max) * 100).toFixed(1) + '%', background: c, borderRadius: 999 }} />
                  </div>
                  <p className="md-truncate" style={{ fontSize: 10.5, color: 'var(--md-text-3)' }}>{s.owner}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Throughput chart row */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) 280px',
            gap: 18,
            padding: '14px 22px 20px',
            alignItems: 'center',
            borderTop: '1px solid var(--md-border-2)',
            background: 'var(--md-surface)',
          }}
        >
          <div className="flex items-center" style={{ gap: 18, flexWrap: 'wrap' }}>
            <div>
              <p className="md-eyebrow">Daily throughput</p>
              <p className="md-tnum" style={{ fontSize: 20, fontWeight: 500, marginTop: 2 }}>
                237 <span style={{ fontSize: 11, color: 'var(--md-text-3)', fontWeight: 400, marginLeft: 4 }}>processed this week</span>
              </p>
            </div>
            <span className="md-divider-v" style={{ height: 30 }} />
            <div>
              <p className="md-eyebrow">SLA met</p>
              <p className="md-tnum" style={{ fontSize: 20, fontWeight: 500, marginTop: 2, color: 'var(--md-emerald)' }}>97.4%</p>
            </div>
            <span className="md-divider-v" style={{ height: 30 }} />
            <div>
              <p className="md-eyebrow">Avg. handle time</p>
              <p className="md-tnum" style={{ fontSize: 20, fontWeight: 500, marginTop: 2 }}>38<span style={{ fontSize: 11, color: 'var(--md-text-3)', marginLeft: 2 }}>min</span></p>
            </div>
          </div>
          <div style={{ height: 64 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={PIPELINE_THROUGHPUT} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                <XAxis dataKey="day" tickLine={false} axisLine={false} fontSize={10} />
                <Tooltip cursor={{ fill: 'rgba(28,31,26,0.04)' }} />
                <Bar dataKey="value" fill="#4A6B5C" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </section>
  );
}
`,

  'src/components/ActivityTimeline.js': `import React from 'react';
import Icon from './Icon';
import { ACTIVITY_EVENTS } from '../data/meridian';

const TONE_HEX = {
  emerald: '#4A6B5C',
  slate:   '#5B6B7D',
  copper:  '#A6755A',
  olive:   '#7A7B47',
};

export default function ActivityTimeline() {
  return (
    <section className="md-rise" style={{ marginTop: 16 }}>
      <div className="md-panel" style={{ padding: 0, overflow: 'hidden' }}>
        <div
          className="flex items-center justify-between"
          style={{ padding: '16px 22px', borderBottom: '1px solid var(--md-border-2)', gap: 12, flexWrap: 'wrap' }}
        >
          <div>
            <p className="md-eyebrow">Activity timeline</p>
            <h3 style={{ fontSize: 15, fontWeight: 500, marginTop: 3 }}>Operational history \\u00B7 today</h3>
          </div>
          <div className="flex items-center" style={{ gap: 6 }}>
            <span className="md-chip">All events</span>
            <span className="md-chip md-chip-emerald">Approvals</span>
            <span className="md-chip md-chip-copper">Flags</span>
            <span className="md-chip md-chip-slate">System</span>
            <button type="button" className="md-btn md-btn-ghost" style={{ fontSize: 11 }}>
              Export <Icon name="export" size={10} />
            </button>
          </div>
        </div>

        <div>
          <div className="md-row" style={{ background: 'var(--md-surface)', padding: '8px 22px', borderBottom: '1px solid var(--md-border-2)' }}>
            <span className="md-label">Time</span>
            <span className="md-label">Event</span>
            <span className="md-label">Detail</span>
            <span className="md-label">Channel</span>
            <span className="md-label">Action</span>
          </div>
          {ACTIVITY_EVENTS.map((e, i) => {
            const c = TONE_HEX[e.tone] || TONE_HEX.slate;
            return (
              <div key={i} className="md-row" style={{ padding: '11px 22px' }}>
                <span className="md-mono" style={{ fontSize: 11, color: 'var(--md-text-2)', letterSpacing: '0.02em' }}>{e.time}</span>
                <div style={{ minWidth: 0 }}>
                  <p className="md-truncate" style={{ fontSize: 12.5 }}>
                    <span style={{ color: 'var(--md-text)', fontWeight: 500 }}>{e.who}</span>
                    <span style={{ color: 'var(--md-text-3)' }}> {e.what}</span>
                  </p>
                </div>
                <span className="md-truncate" style={{ fontSize: 12, color: 'var(--md-text-2)', minWidth: 0, maxWidth: 340 }}>{e.detail}</span>
                <span
                  className="md-chip"
                  style={{
                    height: 19, padding: '0 7px', fontSize: 9.5,
                    background: 'rgba(255,255,255,0.5)',
                    borderColor: c, color: c,
                  }}
                >
                  <span className="md-dot" /> {e.tone}
                </span>
                <button type="button" className="md-btn md-btn-ghost" style={{ height: 22, padding: '0 8px', fontSize: 10.5 }}>
                  Open <Icon name="arrow" size={10} />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
`,

  'src/components/DashboardFooter.js': `import React from 'react';
import Icon from './Icon';
import { SYSTEM_STATUS } from '../data/meridian';

export default function DashboardFooter() {
  return (
    <footer
      className="md-fade"
      style={{
        marginTop: 18,
        padding: '14px 6px 4px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 16,
        borderTop: '1px solid var(--md-border-2)',
        flexWrap: 'wrap',
      }}
    >
      <div className="flex items-center" style={{ gap: 18, flexWrap: 'wrap' }}>
        {SYSTEM_STATUS.map((s, i) => (
          <div key={s.label} className="flex items-center" style={{ gap: 10 }}>
            {i === 0 && (
              <span style={{ position: 'relative', display: 'inline-flex' }}>
                <span style={{ width: 7, height: 7, borderRadius: 999, background: 'var(--md-emerald)' }} />
              </span>
            )}
            <div>
              <p style={{ fontSize: 10, color: 'var(--md-text-3)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{s.label}</p>
              <p className="md-mono" style={{ fontSize: 11.5, color: i === 0 ? 'var(--md-emerald)' : 'var(--md-text-2)' }}>{s.value}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center" style={{ gap: 14, fontSize: 11, color: 'var(--md-text-3)' }}>
        <span>\\u00A9 2026 MERIDIAN \\u00B7 Operations Console</span>
        <span>Privacy</span>
        <span>Audit</span>
        <span>Help</span>
        <span className="flex items-center" style={{ gap: 4 }}>
          <Icon name="globe" size={12} /> EU
        </span>
      </div>
    </footer>
  );
}
`,

  'src/components/AIRail.js': `import React from 'react';
import Icon from './Icon';
import { AI_INSIGHTS, LIVE_TICKER } from '../data/meridian';

const TONE_HEX = {
  emerald: '#4A6B5C',
  slate:   '#5B6B7D',
  copper:  '#A6755A',
  olive:   '#7A7B47',
};

const TONE_SOFT = {
  emerald: 'var(--md-emerald-soft)',
  slate:   'var(--md-slate-soft)',
  copper:  'var(--md-copper-soft)',
  olive:   'var(--md-olive-soft)',
};

const TONE_EDGE = {
  emerald: 'var(--md-emerald-edge)',
  slate:   'var(--md-slate-edge)',
  copper:  'var(--md-copper-edge)',
  olive:   'var(--md-olive-edge)',
};

export default function AIRail() {
  return (
    <aside
      className="md-ai-rail md-scroll"
      style={{
        background: 'var(--md-surface)',
        borderLeft: '1px solid var(--md-border)',
        padding: '20px 18px',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      {/* AI Insights panel */}
      <section className="md-panel md-rise" style={{ padding: '16px 16px 12px' }}>
        <div className="flex items-center justify-between" style={{ gap: 8, marginBottom: 4 }}>
          <div className="flex items-center" style={{ gap: 8 }}>
            <span
              style={{
                width: 24, height: 24, borderRadius: 6,
                background: 'linear-gradient(155deg, #1F221C 0%, #3A3D34 100%)',
                color: '#ECE9DD',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <Icon name="sparkles" size={11} />
            </span>
            <p style={{ fontSize: 13, fontWeight: 500 }}>Operations brief</p>
          </div>
          <span className="md-mono" style={{ fontSize: 10, color: 'var(--md-text-3)', letterSpacing: '0.06em' }}>04:12 PM</span>
        </div>
        <p style={{ fontSize: 11.5, color: 'var(--md-text-3)', marginTop: 2 }}>
          Generated by MERIDIAN intelligence \\u00B7 four signals from the last 24 hours.
        </p>

        <div className="md-divider" style={{ margin: '12px 0' }} />

        <ul style={{ padding: 0, margin: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {AI_INSIGHTS.map((ins) => {
            const c = TONE_HEX[ins.tone] || TONE_HEX.slate;
            return (
              <li
                key={ins.id}
                className="md-panel-flat"
                style={{
                  padding: '11px 12px',
                  borderLeft: '2px solid ' + c,
                  borderTopLeftRadius: 7, borderBottomLeftRadius: 7,
                }}
              >
                <div className="flex items-start justify-between" style={{ gap: 8 }}>
                  <p style={{ fontSize: 12, fontWeight: 500, color: 'var(--md-text)', lineHeight: 1.45 }}>{ins.title}</p>
                  <span
                    className="md-mono"
                    style={{
                      fontSize: 10, color: c,
                      padding: '2px 6px', borderRadius: 4,
                      background: TONE_SOFT[ins.tone],
                      border: '1px solid ' + TONE_EDGE[ins.tone],
                      flexShrink: 0,
                    }}
                  >
                    {ins.confidence}%
                  </span>
                </div>
                <p className="md-clamp-3" style={{ fontSize: 11.5, color: 'var(--md-text-2)', marginTop: 6 }}>{ins.detail}</p>
                <button
                  type="button"
                  className="md-btn md-btn-ghost"
                  style={{ marginTop: 6, height: 22, padding: '0 8px', fontSize: 10.5 }}
                >
                  {ins.action} <Icon name="arrow" size={10} />
                </button>
              </li>
            );
          })}
        </ul>
      </section>

      {/* Live ticker */}
      <section className="md-panel-deep md-rise" style={{ padding: '16px 16px 14px' }}>
        <div className="flex items-center justify-between" style={{ gap: 8 }}>
          <p className="md-eyebrow" style={{ color: 'rgba(236, 233, 221, 0.6)' }}>Live activity</p>
          <span className="flex items-center" style={{ gap: 6, fontSize: 10.5, color: 'rgba(236, 233, 221, 0.7)' }}>
            <span className="md-blink" style={{ width: 6, height: 6, borderRadius: 999, background: '#7E9E8A', display: 'inline-block' }} />
            streaming
          </span>
        </div>
        <ul style={{ padding: 0, margin: '10px 0 0', listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {LIVE_TICKER.map((t, i) => {
            const c = TONE_HEX[t.tone] || TONE_HEX.slate;
            return (
              <li key={i} className="flex items-start" style={{ gap: 10 }}>
                <span
                  style={{
                    marginTop: 6,
                    width: 5, height: 5, borderRadius: 999,
                    background: c,
                    boxShadow: '0 0 0 3px rgba(255,255,255,0.04)',
                    flexShrink: 0,
                  }}
                />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <p className="md-clamp-2" style={{ fontSize: 11.5, color: '#ECE9DD' }}>{t.text}</p>
                  <p className="md-mono" style={{ fontSize: 9.5, color: 'rgba(236,233,221,0.5)', marginTop: 4, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                    {t.type} \\u00B7 {t.when}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>

        <button
          type="button"
          className="md-btn"
          style={{
            marginTop: 12, width: '100%', justifyContent: 'center',
            background: 'rgba(255,255,255,0.06)', color: '#ECE9DD',
            borderColor: 'rgba(255,255,255,0.10)',
          }}
        >
          View full feed <Icon name="arrow" size={11} />
        </button>
      </section>

      {/* Briefing footer */}
      <section className="md-panel md-rise" style={{ padding: '14px 16px' }}>
        <p className="md-eyebrow">Up next</p>
        <p style={{ fontSize: 13, fontWeight: 500, marginTop: 4 }}>Executive brief \\u00B7 05:00 PM</p>
        <p style={{ fontSize: 11.5, color: 'var(--md-text-3)', marginTop: 5, lineHeight: 1.5 }}>
          Daily operations digest is auto-generated at 5pm Geneva time. Routed to leadership and finance.
        </p>
        <div className="flex items-center" style={{ gap: 6, marginTop: 10 }}>
          <button type="button" className="md-btn md-btn-primary" style={{ flex: 1, justifyContent: 'center' }}>
            Preview <Icon name="arrow" size={11} />
          </button>
          <button type="button" className="md-btn" title="Open settings">
            <Icon name="circle" size={11} />
          </button>
        </div>
      </section>
    </aside>
  );
}
`,
};
